import React, { useState, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { BsArrowRightShort, BsCheck, BsX } from "react-icons/bs";
import { ProductColors } from './ProductColors';

import '../style/AddedProductsInCheckout.scss';



const AddedProductsInCheckout = () => {
    let timeoutId;
    const { t } = useTranslation();

    const [itemInCart, setItemInCart] = useState({ products: [] });
    const [discountApplied, setDiscountApplied] = useState(false);
    const [discountError, setDiscountError] = useState(false);
    const [discount, setDiscount] = useState('');
    const [comment, setComment] = useState('');
    const commentRef = useRef(null);
    
    const fetchCartData = () => {
        fetch('/Api/System/Shop/Session/Cart/GetBasicCartData')
            .then(response => response.json())
            .then(data => {
                setItemInCart(data);

                // Handle discount
                if (data.discountCode && data.discountCode.trim() !== '') {
                    setDiscountApplied(true);
                    setDiscount(data.discountCode);
                } else {
                    setDiscountApplied(false);
                }

                // Handle additionalNote
                if (data.additionalNote && data.additionalNote.trim() !== '') {
                    setComment(data.additionalNote);
                } else {
                    setComment('');
                }
            });
    };

    useEffect(() => {
        fetchCartData();
    }, []);

    useEffect(() => {
        if (itemInCart.additionalNote) {
            setComment(itemInCart.additionalNote);
        }
    }, [itemInCart.additionalNote]);

    useEffect(() => {
        if (commentRef.current) {
            commentRef.current.style.height = 'auto'; // Reset
            commentRef.current.style.height = `${commentRef.current.scrollHeight}px`; // Resize to fit content
        }
    }, [comment]);

    const handleQuantityChange = async (id, type) => {
        const url = type === 'increase'
            ? '/Api/System/Shop/Session/Cart/AddProduct'
            : '/Api/System/Shop/Session/Cart/RemoveProduct';

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: 'productId=' + id + '&quantity=1'
            });

            if (!response.ok) {
                throw new Error(`Failed to update cart: ${response.statusText}`);
            }
            fetchCartData();
        } catch (error) {
            console.error('Error updating quantity:', error);
        }
    };

    const handleChangeDiscount = (e) => {
        const { value } = e.target;
        setDiscount(value);
        setDiscountApplied(false);
    };

    const handleKeyDownDiscount = (e) => {
        if (e.key === 'Enter') {
            handleSubmitDiscount();
        }
    };

    const handleSubmitDiscount = async (remove = false) => {

        if (!remove && (!discount || discount.trim() === "")) {
            // Don't send if trying to add empty discount
            setDiscountError(true);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('code', remove ? "" : discount);

            const response = await fetch('/Api/System/Shop/Session/SetDiscount', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Failed to set discount: ${response.statusText}`);
            }

            const data = await response.json();

            if (data === true) {
                if (remove) {
                    setDiscount('');
                    setDiscountApplied(false);
                    setDiscountError(false);
                    setItemInCart(prev => ({
                        ...prev,
                        discountCode: ''
                    }));
                } else {
                    setDiscountApplied(true);
                    setDiscountError(false);
                }
                fetchCartData();
            } else {
                setDiscountApplied(false);
                setDiscountError(true);
            }

        } catch (error) {
            console.error('Error setting discount:', error);
            setDiscountApplied(false);
            setDiscountError(true);
        }
    };

    const handleSubmitComment = async (newComment = comment) => {

        try {
            const formData = new FormData();
            formData.append('additionalNote', newComment);

            await fetch('/Api/System/Shop/Session/Customer/Set', {
                method: 'POST',
                body: formData,
            });

            fetchCartData();
        } catch (error) {
            console.error('Error saving comment:', error);
        }
    };

    // --- AUTOSAVE AFTER DELAY ---
    useEffect(() => {
        clearTimeout(timeoutId);

        timeoutId = setTimeout(() => {
            handleSubmitComment(comment);
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [comment]);


    return (
        <div className='added-products-container'>
            <div className='row'>
                <div className='col-12'>
                    <div className='added-products-header'>
                        <p>{t('shop-AddedProducts')}</p>
                    </div>
                    {itemInCart.products.length > 0 ? (
                        <>
                            <div className='added-products-div'>
                                {itemInCart.products.map((product) => {

                                    const colorPropertiesId = product.properties
                                        .filter(prop => prop.type === 'color')
                                        .map(prop => prop.propertyId);

                                    const sizeValue = product.properties
                                        .filter(prop => prop.type === 'size')
                                        .map(prop => prop.value);

                                    return (
                                        <div key={product.productId} className="added-product">
                                            <div className="added-product-image">
                                                <img src={product.image} alt={product.name} />
                                            </div>
                                            <div className="added-product-details">
                                                <div className="added-product-details-row">
                                                    <h2 className="added-product-title">{product.name}</h2>
                                                    <div className="added-product-color">
                                                        {/* Displaying color circles based on propertyId */}
                                                        {colorPropertiesId.map((propertyId, index) => {
                                                            const color = ProductColors[propertyId];

                                                            if (color) {
                                                                // If it's an array (multiple colors), render each color as a split circle
                                                                if (Array.isArray(color)) {
                                                                    return (
                                                                        <div
                                                                            key={index}
                                                                            className="color-circle-split"
                                                                            style={{
                                                                                background: `linear-gradient(to right, ${color[0]} 50%, ${color[1]} 50%)`
                                                                            }}
                                                                            aria-label={`Color: ${color[0]} / ${color[1]}`}
                                                                        />
                                                                    );
                                                                }
                                                                // If it's a single color, render just that one color
                                                                return (
                                                                    <div
                                                                        key={index}
                                                                        className="color-circle"
                                                                        style={{ backgroundColor: color }}
                                                                        aria-label={`Color: ${color}`}
                                                                    />
                                                                );
                                                            }
                                                            return null;
                                                        })}
                                                    </div>
                                                </div>

                                                <p className="added-product-price">{product.displayPrice}</p>
                                                <div className="size-box">
                                                    {sizeValue}
                                                </div>

                                                <div className="added-product-counter">
                                                    <button onClick={() => handleQuantityChange(product.productId, 'decrease')} className="counter-button">-</button>
                                                    <span className="added-product-counter-value">{product.quantity}</span>
                                                    <button onClick={() => handleQuantityChange(product.productId, 'increase')} className="counter-button">+</button>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            <div style={{ justifyContent: 'center', borderTop: '1px solid #D9D9D9' }} />
                            <div className='added-products-input-container'>
                                <div className='added-products-discountcode-container'>
                                    {discountApplied ? (
                                        <>
                                            <div className='added-products-discountcode'>
                                                <div className='added-products-discount'>
                                                    <p>{t('shop-CouponCode')}:</p>
                                                    <p style={{ fontFamily: 'Monsterrat-Bold' }}>{itemInCart.discountCode}</p>
                                                </div>
                                                <BsX
                                                    className="added-products-input-arrow-right"
                                                    size={25}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSubmitDiscount(true)}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className='added-products-input-fields'>
                                                <input
                                                    className='added-products-input-discount'
                                                    type="text"
                                                    value={discount}
                                                    onChange={handleChangeDiscount}
                                                    onKeyDown={handleKeyDownDiscount}
                                                    placeholder={t('shop-Discount')}
                                                />
                                                <BsArrowRightShort
                                                    className='added-products-input-arrow-right'
                                                    size={25}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSubmitDiscount(false)}
                                                />
                                            </div>
                                            {discountError && (
                                                <div className='added-products-discountcode-error'>
                                                    <div className='added-products-discount-error'>
                                                        <p style={{ color: 'red', marginBottom: '14px' }}>
                                                            {t('shop-DiscountError')}
                                                        </p>
                                                    </div>
                                                    <BsX
                                                        className="added-products-input-arrow-right-error"
                                                        size={25}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className='added-products-message-container'>
                                    <div className='added-products-input-fields'>
                                        <textarea
                                            ref={commentRef}
                                            className='added-products-input-discount'
                                            type="text"
                                            value={comment}
                                            onChange={(e) => {
                                                setComment(e.target.value);
                                            }}
                                            placeholder={t('shop-Message')}
                                            rows={1}
                                        />
                                        <BsArrowRightShort
                                            className='added-products-input-arrow-right'
                                            size={25}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div style={{ justifyContent: 'center', borderTop: '1px solid #D9D9D9' }} />
                            <div className='added-products-price-and-shipping'>
                                <p>{t('shop-TotalProducts')}</p>
                                <p>{itemInCart.displayFinalValue}</p>
                            </div>

                        </>
                    ) : (
                        <p className="added-products-empty-cart">{t('shop-cartEmpty')}</p>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AddedProductsInCheckout;